import { format, isToday, isYesterday } from 'date-fns';

/**
 * Parse a timestamp from the backend as UTC.
 * Backend stores all times in UTC but may omit the 'Z' suffix.
 * This ensures consistent interpretation regardless of format.
 */
export function parseUTC(timestamp) {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  const str = String(timestamp);
  // If no timezone indicator, append 'Z' to force UTC interpretation
  if (!str.endsWith('Z') && !str.includes('+') && !/\d{2}:\d{2}$/.test(str.slice(-6))) {
    return new Date(str + 'Z');
  }
  return new Date(str);
}

/**
 * Format a UTC timestamp for display in user's local timezone.
 * @param {string|Date} timestamp - UTC timestamp from backend
 * @param {string} fmt - date-fns format string (default: 'MMM dd, yyyy HH:mm')
 * @returns {string} Formatted local time string or '-' if invalid
 */
export function formatLocal(timestamp, fmt = 'MMM dd, yyyy HH:mm') {
  const d = parseUTC(timestamp);
  if (!d || isNaN(d.getTime())) return '-';
  return format(d, fmt);
}

/**
 * Format time only (e.g., "14:30")
 */
export function formatLocalTime(timestamp, fmt = 'HH:mm') {
  return formatLocal(timestamp, fmt);
}

/**
 * Format date only (e.g., "Mar 17, 2026")
 */
export function formatLocalDate(timestamp, fmt = 'MMM dd, yyyy') {
  return formatLocal(timestamp, fmt);
}

/**
 * Get group label for activity feed date grouping.
 * Uses local timezone for "Today" / "Yesterday" determination.
 */
export function getDateGroupLabel(timestamp) {
  const d = parseUTC(timestamp);
  if (!d || isNaN(d.getTime())) return 'Unknown';
  if (isToday(d)) return 'Today';
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'MMMM dd, yyyy');
}
